import { useEffect, useState, useCallback, useRef } from "react";
import { GooglePayConfig } from "services/google-pay-service.ts";
import { postRequest } from "utils/httpClient.ts";
import { toast } from "react-toastify";

export enum PaymentStatus {
  SUCCESS,
  FAILURE
}

interface GooglePayButtonProps {
  amount: number;
  onPaymentAuthorized: (googlePayToken: string) => Promise<PaymentStatus>;
  onVisibilityChange?: (isVisible: boolean) => void;
}

const GooglePayButton = ({ amount, onPaymentAuthorized, onVisibilityChange }: GooglePayButtonProps) => {
  const domainName = window.location.hostname;
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [merchantConfig, setMerchantConfig] = useState<GooglePayConfig | null>(null);
  const [isReadyToPay, setIsReadyToPay] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const paymentsClientRef = useRef<any>(null);

  const handleGooglePayClick = useCallback(async () => {
    console.log("[GooglePay] Button clicked");

    if (isProcessing || !merchantConfig) {
      console.log("[GooglePay] Cannot process click - processing:", isProcessing, "config:", !!merchantConfig);
      return;
    }

    if (!amount || amount <= 0) {
      console.log("[GooglePay] Invalid amount:", amount);
      toast.error("Please select a valid amount");
      return;
    }

    setIsProcessing(true);
    console.log("[GooglePay] Processing payment for amount:", amount);

    try {
      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
          type: "CARD",
          parameters: {
            allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
            allowedCardNetworks: ["AMEX", "DISCOVER", "MASTERCARD", "VISA"],
          },
          tokenizationSpecification: {
            type: "PAYMENT_GATEWAY",
            parameters: {
              gateway: merchantConfig.hostConfig.gatewayId,
              gatewayMerchantId: merchantConfig.hostConfig.gatewayMerchantId,
            },
          },
        }],
        merchantInfo: {
          merchantId: merchantConfig.hostConfig.merchantId,
          merchantName: merchantConfig.hostConfig.merchantName,
        },
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPrice: amount.toFixed(2),
          currencyCode: "USD",
          countryCode: "US",
        },
      };

      console.log("[GooglePay] Payment data request:", JSON.stringify(paymentDataRequest, null, 2));
      const paymentData = await paymentsClientRef.current.loadPaymentData(paymentDataRequest);
      console.log("[GooglePay] Payment authorized:", JSON.stringify(paymentData, null, 2));

      // Extract token from response
      const token = paymentData.paymentMethodData.tokenizationData.token;
      const tokenEncoded = btoa(token);
      console.log("[GooglePay] Token encoded, length:", tokenEncoded.length);

      const status = await onPaymentAuthorized(tokenEncoded);
      console.log("[GooglePay] Payment result:", status);
    } catch (error: any) {
      if (error.statusCode === "CANCELED") {
        console.log("[GooglePay] Payment canceled by user");
      } else {
        console.error("[GooglePay] Payment failed:", error);
        toast.error("Google Pay payment failed. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  }, [amount, isProcessing, merchantConfig, onPaymentAuthorized]);

  // Notify parent of visibility changes
  useEffect(() => {
    onVisibilityChange?.(isReadyToPay);
  }, [isReadyToPay, onVisibilityChange]);

  // Create button after isReadyToPay confirmed
  useEffect(() => {
    if (!merchantConfig || !isReadyToPay || !buttonContainerRef.current || !paymentsClientRef.current) return;

    const button = paymentsClientRef.current.createButton({
      buttonColor: "black",
      buttonType: "plain",
      buttonSizeMode: "fill",
      onClick: handleGooglePayClick,
    });

    buttonContainerRef.current.innerHTML = "";
    buttonContainerRef.current.appendChild(button);
    console.log("[GooglePay] Button created and appended");
  }, [merchantConfig, isReadyToPay, handleGooglePayClick]);

  // Fetch config on mount and check isReadyToPay
  useEffect(() => {
    console.log("[GooglePay] Initializing with domain:", domainName);
    console.log("[GooglePay] Current protocol:", window.location.protocol);

    const google = (window as any).google;
    if (!google?.payments?.api?.PaymentsClient) {
      console.warn("[GooglePay] Google Pay library not loaded");
      setIsLoading(false);
      return;
    }

    console.log("[GooglePay] Google Pay library detected, fetching config...");
    postRequest("/v2/commerce/inovio/google-pay/get-config", {
      body: { domainName }
    })
      .then(async config => {
        console.log("[GooglePay] Received config:", JSON.stringify(config, null, 2));
        if (!config || !config.hostConfig) {
          console.error("[GooglePay] Received invalid config structure:", config);
          throw new Error("Invalid Google Pay configuration received from server.");
        }

        // Create payments client to check isReadyToPay
        const googlePayEnvironment = "TEST"
        const paymentsClient = new google.payments.api.PaymentsClient({
          environment: googlePayEnvironment,
        });

        // Check if Google Pay is ready for this user/device
        const isReadyToPayRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["AMEX", "DISCOVER", "MASTERCARD", "VISA"],
            },
          }],
        };

        console.log("[GooglePay] Checking isReadyToPay...");
        const response = await paymentsClient.isReadyToPay(isReadyToPayRequest);
        console.log("[GooglePay] isReadyToPay response:", response);

        if (response.result) {
          setMerchantConfig(config);
          setIsReadyToPay(true);
          paymentsClientRef.current = paymentsClient;
          console.log("[GooglePay] Ready to pay - button will be shown.");
        } else {
          console.log("[GooglePay] Not ready to pay for this user/device.");
        }
        setIsLoading(false);
      })
      .catch(e => {
        // Silently fail if backend doesn't support Google Pay (404) or returns an error
        // This prevents showing confusing error toasts when the feature isn't implemented
        console.warn("[GooglePay] Config not available:", e?.response?.status || e.message);
        setIsLoading(false);
      });
  }, [domainName]);

  if (isLoading) {
    return null;
  }

  if (!merchantConfig || !isReadyToPay) {
    return null;
  }

  const isDisabled = isProcessing || !amount || amount <= 0;

  return (
    <>
      <div
        ref={buttonContainerRef}
        className="google-pay-button-container"
        style={{
          opacity: isDisabled ? 0.5 : 1,
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
          pointerEvents: isDisabled ? 'none' : 'auto'
        }}
      />
      <style>
        {`
          .google-pay-button-container button {
            width: 100% !important;
            height: 50px !important;
            border-radius: 8px !important;
          }

          @media (max-width: 768px) {
            .google-pay-button-container button {
              height: 48px !important;
              border-radius: 6px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default GooglePayButton;
